import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { useInvoiceProductsByAreaQuery } from '@/hooks/billing/useInvoiceProductsByAreaQuery'
import { useBusinessConfigsQuery } from '@/hooks/business/useBusinessConfigsQuery'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useSalesCategoriesQuery } from '@/hooks/business/useSalesCategoriesQuery'
import { useDebounce } from '@/hooks/useDebounce'
import { InvoiceFullProductData } from '@/interfaces/billing'
import { SelledProductsInterface } from '@/interfaces/ServerInterfaces'
import { getProductMainCurrency, getProductMainPrice } from '@/utils/priceUtils'

import { DocumentType } from './newRegisterWizard/NewRegisterWizard'
import { ManagerStock } from './productManagerSections/ManagerStock'
import { ProductActionsView } from './productManagerSections/ProductActionsView'
import { TipsPanel } from './productManagerSections/TipsPanel'

// Interface for the component props
interface ProductManagerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingProducts: SelledProductsInterface[]
  onSave: (actions: ProductManagerActions, areaSalesId: number) => void
  documentType: DocumentType
  priceSystemId: number
  overlayClassName?: string
  isTemporaryChanges?: boolean // true for wizard mode, false for real database changes
  selectedPointOfSale: number | null
}

// Action types for product management
interface AddProductAction {
  type: 'ADD'
  tempId: string
  product: InvoiceFullProductData
  quantity: number
  priceUnitary: { amount: number; codeCurrency: string }
}

interface ModifyProductAction {
  type: 'MODIFY'
  originalProduct: SelledProductsInterface
  newQuantity: number
  newPrice: number
  currency: string
}

interface DeleteProductAction {
  type: 'DELETE'
  originalProduct: SelledProductsInterface
  quantity: number // quantity to delete (can be partial)
}

// Interface for the actions being managed
interface ProductManagerActions {
  addActions: AddProductAction[]
  modifyActions: ModifyProductAction[]
  deleteActions: DeleteProductAction[]
}

// Interface for stock information
interface StockInfo {
  shouldShowStock: boolean
  stockReal: number
  stockReserved: number
  stockAvailable: number
  shouldShowProduct: boolean
}

// Enhanced interface for stock information considering visual updates
interface EnhancedStockInfo extends StockInfo {
  visualStockReal: number
  visualStockReserved: number
  visualStockAvailable: number
}

// Utility function to format stock numbers based on measure type
const formatStockNumber = (value: number, measure?: string): string => {
  if (measure === 'UNIT') {
    return value.toString()
  }
  return value.toFixed(2)
}

export function ProductManager({
  open,
  onOpenChange,
  existingProducts,
  onSave,
  overlayClassName,
  documentType,
  isTemporaryChanges = false,
  priceSystemId,
  selectedPointOfSale,
}: ProductManagerProps) {
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})

  const { businessConfigs, isLoadingBusinessConfigs, isErrorBusinessConfigs } =
    useBusinessConfigsQuery({
      params: {
        businessId: businessData?.id ?? 0,
      },
      options: {
        enabled:
          !!businessData?.id &&
          !isLoadingBusinessData &&
          !isErrorBusinessData &&
          open,
      },
    })

  const enableToSaleInNegative = useMemo(() => {
    return (
      businessConfigs?.find(
        (config) => config.key === 'enable_to_sale_in_negative'
      )?.value === 'true' || false
    )
  }, [businessConfigs])

  const saleByGlobalDisponibility = useMemo(() => {
    return (
      businessConfigs?.find(
        (config) => config.key === 'sale_by_global_disponibility'
      )?.value === 'true' || false
    )
  }, [businessConfigs])

  const enableToCheckStockInPreorder = useMemo(() => {
    return (
      businessConfigs?.find(
        (config) => config.key === 'enable_to_check_stock_in_preorder'
      )?.value === 'true' || false
    )
  }, [businessConfigs])

  const saleByReservation = useMemo(() => {
    return (
      businessConfigs?.find((config) => config.key === 'sale_by_reservation')
        ?.value === 'true' || false
    )
  }, [businessConfigs])

  const subtractProductsFromManufacturerArea = useMemo(() => {
    return (
      businessConfigs?.find(
        (config) => config.key === 'substract_products_from_manufacturer_area'
      )?.value === 'true' || false
    )
  }, [businessConfigs])

  /**
   * Helper function to determine if product type should use global stock rules
   *
   * These product types (COMBO, MENU, ADDON, SERVICE) are typically:
   * - Composed of other products or are services
   * - Managed globally across the business
   * - Use totalQuantity for availability checking in FACTURA
   * - Use totalReserved for reservation checking in PREFACTURA
   *
   * @param productType - The product type to check
   * @returns true if the product should use global stock rules
   */
  const isGlobalStockProduct = useCallback((productType: string): boolean => {
    return ['COMBO', 'MENU', 'ADDON', 'SERVICE'].includes(productType)
  }, [])

  /**
   * Helper function to determine if product type should use area-specific stock rules
   *
   * These product types (STOCK, VARIATION) are typically:
   * - Physical inventory items
   * - Managed per warehouse/area
   * - Subject to global vs local availability business rules
   * - Follow the sale_by_global_disponibility configuration
   *
   * @param productType - The product type to check
   * @returns true if the product should use area-specific stock rules
   */
  const isAreaStockProduct = useCallback((productType: string): boolean => {
    return ['STOCK', 'VARIATION'].includes(productType)
  }, [])

  /**
   * Helper function to calculate stock information for a product based on comprehensive business rules
   *
   * This function implements the complete business logic for stock availability checking
   * as defined in the business documentation for both FACTURA and PREFACTURA document types.
   *
   * BUSINESS RULES IMPLEMENTED:
   *
   * FOR PREFACTURA (Pre-invoices):
   * 1. If enable_to_check_stock_in_preorder = false → No stock checking (infinite stock)
   * 2. If product.stockLimit = false → No reservation checking
   * 3. For COMBO, MENU, ADDON, SERVICE with stockLimit = true → Verify from totalReserved
   * 4. For STOCK, VARIATION:
   *    4a. If sale_by_global_disponibility AND sale_by_reservation = true → Check global reserved capacity
   *    4b. If sale_by_reservation = false → Show reserved without restriction (default)
   *    5. Otherwise → Check reservation in point-of-sale warehouse
   *
   * FOR FACTURA (Invoices):
   * 1. If enable_to_sale_in_negative = true → No stock checking (infinite stock)
   * 2. If product.stockLimit = false → No stock checking
   * 3. For COMBO, MENU, ADDON, SERVICE with stockLimit = true → Verify from totalQuantity
   * 4. For STOCK, VARIATION with sale_by_global_disponibility = true → Check global availability
   * 5. For STOCK, VARIATION with sale_by_global_disponibility = false → Check point-of-sale warehouse
   * 6. Special case: substract_products_from_manufacturer_area = true → Products go to reserved state
   *    (Note: This special case is handled at the backend level during invoice processing)
   *
   * @param product - The product to calculate stock information for
   * @returns StockInfo object with stock values and display preferences
   */
  const getStockInfo = useCallback(
    (product: InvoiceFullProductData): StockInfo => {
      // PREFACTURA RULES
      if (documentType === 'prefactura') {
        // PREFACTURA Rule 1: if enable_to_check_stock_in_preorder === false, no stock checking (infinite stock)
        if (!enableToCheckStockInPreorder) {
          return {
            shouldShowStock: false,
            stockReal: 0,
            stockReserved: 0,
            stockAvailable: 0,
            shouldShowProduct: true, // Always show product when stock check is disabled for preorders
          }
        }

        // PREFACTURA Rule 2: if product.stockLimit === false, no reservation checking
        if (!product.stockLimit) {
          return {
            shouldShowStock: false,
            stockReal: 0,
            stockReserved: 0,
            stockAvailable: 0,
            shouldShowProduct: true, // Always show product when no stock limit
          }
        }

        // PREFACTURA Rule 3: For COMBO, MENU, ADDON, SERVICE with stockLimit === true, verify from totalReserved
        if (isGlobalStockProduct(product.type) && product.stockLimit) {
          const stockReal = Number(product?.totalQuantity) || 0
          const stockReserved = Number(product?.totalReserved) || 0
          const stockAvailable = stockReal - stockReserved

          return {
            shouldShowStock: true,
            stockReal: Number(stockReal.toFixed(2)),
            stockReserved: Number(stockReserved.toFixed(2)),
            stockAvailable: Number(stockAvailable.toFixed(2)),
            shouldShowProduct: stockReal > 0, // Show product if totalQuantity > 0
          }
        }

        // PREFACTURA Rule 4: For STOCK and VARIATION products
        if (isAreaStockProduct(product.type)) {
          // PREFACTURA Rule 4a: if sale_by_global_disponibility AND sale_by_reservation are active
          if (saleByGlobalDisponibility && saleByReservation) {
            const stockReal = Number(product?.totalQuantity) || 0
            const stockReserved = Number(product?.totalReserved) || 0
            const stockAvailable = stockReal - stockReserved

            return {
              shouldShowStock: true,
              stockReal: Number(stockReal.toFixed(2)),
              stockReserved: Number(stockReserved.toFixed(2)),
              stockAvailable: Number(stockAvailable.toFixed(2)),
              shouldShowProduct: stockAvailable > 0, // Show product if available reservation capacity exists
            }
          }

          // PREFACTURA Rule 4b: if sale_by_reservation is not active, just show that product was reserved (default behavior)
          if (!saleByReservation) {
            const stockReal = Number(product?.totalQuantity) || 0
            const stockReserved = Number(product?.totalReserved) || 0
            const stockAvailable = stockReal - stockReserved

            return {
              shouldShowStock: true,
              stockReal: Number(stockReal.toFixed(2)),
              stockReserved: Number(stockReserved.toFixed(2)),
              stockAvailable: Number(stockAvailable.toFixed(2)),
              shouldShowProduct: true, // Always show product when reservation checking is disabled
            }
          }

          // PREFACTURA Rule 5: Check reservation in point-of-sale warehouse
          if (
            !product.stockAreaProducts ||
            product.stockAreaProducts.length === 0
          ) {
            return {
              shouldShowStock: true,
              stockReal: 0,
              stockReserved: 0,
              stockAvailable: 0,
              shouldShowProduct: false, // No stockAreaProduct, don't show product
            }
          }

          const stockAreaProduct = product.stockAreaProducts[0]
          const stockReal = Number(stockAreaProduct?.quantity) || 0
          const stockReserved = Number(stockAreaProduct?.reserved) || 0
          const stockAvailable = stockReal - stockReserved

          return {
            shouldShowStock: true,
            stockReal: Number(stockReal.toFixed(2)),
            stockReserved: Number(stockReserved.toFixed(2)),
            stockAvailable: Number(stockAvailable.toFixed(2)),
            shouldShowProduct: stockAvailable > 0, // Show if reservation capacity exists
          }
        }
      }

      // FACTURA RULES
      if (documentType === 'factura') {
        // FACTURA Rule 1: if enable_to_sale_in_negative === true, no stock checking (infinite stock)
        if (enableToSaleInNegative) {
          return {
            shouldShowStock: false,
            stockReal: 0,
            stockReserved: 0,
            stockAvailable: 0,
            shouldShowProduct: true, // Always show product when negative sales are enabled
          }
        }

        // FACTURA Rule 2: if product.stockLimit === false, no stock checking
        if (!product.stockLimit) {
          return {
            shouldShowStock: false,
            stockReal: 0,
            stockReserved: 0,
            stockAvailable: 0,
            shouldShowProduct: true, // Always show product when no stock limit
          }
        }

        // FACTURA Rule 3: For COMBO, MENU, ADDON, SERVICE with stockLimit === true, verify from totalQuantity
        if (isGlobalStockProduct(product.type) && product.stockLimit) {
          const stockReal = Number(product?.totalQuantity) || 0
          const stockReserved = Number(product?.totalReserved) || 0
          const stockAvailable = stockReal - stockReserved

          return {
            shouldShowStock: true,
            stockReal: Number(stockReal.toFixed(2)),
            stockReserved: Number(stockReserved.toFixed(2)),
            stockAvailable: Number(stockAvailable.toFixed(2)),
            shouldShowProduct: stockReal > 0, // Show product if totalQuantity > 0
          }
        }

        // FACTURA Rule 4: For STOCK and VARIATION products with global availability
        if (isAreaStockProduct(product.type) && saleByGlobalDisponibility) {
          const stockReal = Number(product?.totalQuantity) || 0
          const stockReserved = Number(product?.totalReserved) || 0
          const stockAvailable = stockReal - stockReserved

          return {
            shouldShowStock: true,
            stockReal: Number(stockReal.toFixed(2)),
            stockReserved: Number(stockReserved.toFixed(2)),
            stockAvailable: Number(stockAvailable.toFixed(2)),
            shouldShowProduct: stockReal > 0, // Show product if global availability exists
          }
        }

        // FACTURA Rule 5: Check availability in point-of-sale associated warehouse (default behavior)
        if (isAreaStockProduct(product.type) && !saleByGlobalDisponibility) {
          if (
            !product.stockAreaProducts ||
            product.stockAreaProducts.length === 0
          ) {
            return {
              shouldShowStock: true,
              stockReal: 0,
              stockReserved: 0,
              stockAvailable: 0,
              shouldShowProduct: false, // No stockAreaProduct, don't show product
            }
          }

          const stockAreaProduct = product.stockAreaProducts[0]
          const stockReal = Number(stockAreaProduct?.quantity) || 0
          const stockReserved = Number(stockAreaProduct?.reserved) || 0
          const stockAvailable = stockReal - stockReserved

          return {
            shouldShowStock: true,
            stockReal: Number(stockReal.toFixed(2)),
            stockReserved: Number(stockReserved.toFixed(2)),
            stockAvailable: Number(stockAvailable.toFixed(2)),
            shouldShowProduct: stockAvailable > 0, // Show if availability exists
          }
        }
      }

      // Default fallback - should not reach here in normal operation
      return {
        shouldShowStock: false,
        stockReal: 0,
        stockReserved: 0,
        stockAvailable: 0,
        shouldShowProduct: true,
      }
    },
    [
      enableToSaleInNegative,
      saleByGlobalDisponibility,
      enableToCheckStockInPreorder,
      saleByReservation,
      subtractProductsFromManufacturerArea,
      documentType,
      isGlobalStockProduct,
      isAreaStockProduct,
    ]
  )

  // State management - new action-based approach
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [actions, setActions] = useState<ProductManagerActions>({
    addActions: [],
    modifyActions: [],
    deleteActions: [],
  })
  const [isInitialized, setIsInitialized] = useState(false)
  const [focusedProductId, setFocusedProductId] = useState<number | undefined>()

  // Debounced search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500)

  // API call to fetch all products for the selected area with infinite loading
  const {
    products: rawProducts,
    isLoadingProducts,
    isErrorProducts,
    isFetchingNextPage,
    canLoadMore,
    fetchNextPage,
    ensureProductLoaded,
    refetchProducts,
  } = useInvoiceProductsByAreaQuery({
    params: {
      areaId: Number(selectedPointOfSale),
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      ...(selectedCategory !== 'all' && {
        salesCategoryId: Number(selectedCategory),
      }),
      priceSystemId: priceSystemId,
    },
    options: {
      enabled:
        !!selectedPointOfSale &&
        Number(selectedPointOfSale) > 0 &&
        !!priceSystemId &&
        open &&
        !!businessData?.id,
    },
    businessId: businessData?.id ?? 0,
  })

  // Filter products based on stock rules
  const products = useMemo(() => {
    if (!rawProducts) return undefined

    return rawProducts.filter((product) => {
      const stockInfo = getStockInfo(product)
      return stockInfo.shouldShowProduct
    })
  }, [rawProducts, getStockInfo])

  // Fetch sales categories for the business
  const { salesCategories, isLoadingSalesCategories, isErrorSalesCategories } =
    useSalesCategoriesQuery({
      params: {
        businessId: businessData?.id ?? 0,
      },
      options: {
        enabled: !!businessData?.id && open,
      },
    })

  // Reset actions when drawer closes
  useEffect(() => {
    if (!open) {
      setActions({
        addActions: [],
        modifyActions: [],
        deleteActions: [],
      })
      setIsInitialized(false)
      setFocusedProductId(undefined)
    } else if (open && !isInitialized) {
      setIsInitialized(true)
    }
  }, [open, isInitialized])

  // Reset category when area changes
  useEffect(() => {
    setSelectedCategory('all')
    setSearchTerm('')
    setActions({
      addActions: [],
      modifyActions: [],
      deleteActions: [],
    })
    setFocusedProductId(undefined)
  }, [selectedPointOfSale])

  // Calculate available stock considering all actions - now using the new stock logic
  const getAvailableStock = useCallback(
    (productId: number): number => {
      const product = products?.find((p) => p.id === productId)
      if (!product) return 0

      const stockInfo = getStockInfo(product)

      // If stock shouldn't be shown (unlimited stock scenarios), return high number
      if (!stockInfo.shouldShowStock) {
        return 999999
      }

      // Start with available stock from business rules
      let availableStock = stockInfo.stockAvailable

      // Calculate the change in stock consumption from all actions
      // Similar logic to getEnhancedStockInfo but for available stock calculation

      // 1. Calculate existing products baseline and final consumption
      const existingProductsForThisProduct = existingProducts.filter(
        (p) => p.productId === productId
      )

      let currentExistingQuantity = 0
      let finalExistingQuantity = 0

      existingProductsForThisProduct.forEach((existingProduct) => {
        const deleteAction = actions.deleteActions.find(
          (action) => action.originalProduct.id === existingProduct.id
        )
        const modifyAction = actions.modifyActions.find(
          (action) => action.originalProduct.id === existingProduct.id
        )

        // Current quantity from this existing product
        currentExistingQuantity += existingProduct.quantity

        // Final quantity after actions
        if (deleteAction) {
          // Being deleted: remaining quantity after deletion
          finalExistingQuantity += Math.max(
            0,
            existingProduct.quantity - deleteAction.quantity
          )
        } else if (modifyAction) {
          // Being modified: new quantity
          finalExistingQuantity += modifyAction.newQuantity
        } else {
          // Not changed: same quantity
          finalExistingQuantity += existingProduct.quantity
        }
      })

      // 2. Calculate from add actions
      const addActionsForProduct = actions.addActions.filter(
        (action) => action.product.id === productId
      )
      const totalAddedQuantity = addActionsForProduct.reduce(
        (sum, action) => sum + action.quantity,
        0
      )

      // 3. Calculate net change based on mode (same logic as getEnhancedStockInfo)
      let totalConsumptionChange = 0
      if (isTemporaryChanges) {
        // TEMPORARY MODE: Existing products are pre-selected items, treat as consumption
        totalConsumptionChange = finalExistingQuantity + totalAddedQuantity
      } else {
        // REAL CHANGES MODE: Only consider the change from current state
        const existingProductsChange =
          finalExistingQuantity - currentExistingQuantity
        totalConsumptionChange = existingProductsChange + totalAddedQuantity
      }

      // 4. Apply total consumption change to available stock
      availableStock -= totalConsumptionChange

      return Math.max(0, availableStock)
    },
    [products, actions, existingProducts, getStockInfo, isTemporaryChanges]
  )

  // Helper function to get full product data
  const getFullProductData = useCallback(
    (productId: number): InvoiceFullProductData | undefined => {
      return products?.find((p) => p.id === productId)
    },
    [products]
  )

  // Handle adding a product from stock
  const handleAddProduct = useCallback(
    (product: InvoiceFullProductData, quantityToAdd: number = 1) => {
      const mainPrice = getProductMainPrice(product, priceSystemId)
      const mainCurrency = getProductMainCurrency(product, priceSystemId)

      // Find existing add action for this product
      const existingAddActionIndex = actions.addActions.findIndex(
        (action) => action.product.id === product.id
      )

      if (existingAddActionIndex >= 0) {
        // Increase quantity in existing add action
        const updatedActions = { ...actions }

        updatedActions.addActions[existingAddActionIndex].quantity +=
          quantityToAdd
        setActions(updatedActions)
      } else {
        // Create new add action
        const newAddAction: AddProductAction = {
          type: 'ADD',
          tempId: `temp-${Date.now()}-${Math.random()}`,
          product,
          quantity: quantityToAdd,
          priceUnitary: {
            amount: mainPrice,
            codeCurrency: mainCurrency,
          },
        }

        setActions((prev) => ({
          ...prev,
          addActions: [...prev.addActions, newAddAction],
        }))
      }
    },
    [getStockInfo, getAvailableStock, priceSystemId, actions]
  )

  // Handle removing an add action
  const handleRemoveAddAction = useCallback((tempId: string) => {
    setActions((prev) => ({
      ...prev,
      addActions: prev.addActions.filter((action) => action.tempId !== tempId),
    }))
  }, [])

  // Handle updating quantity for add action
  const handleUpdateAddQuantity = useCallback(
    (tempId: string, newQuantity: number) => {
      if (newQuantity <= 0) {
        handleRemoveAddAction(tempId)
        return
      }

      setActions((prev) => {
        const updatedActions = { ...prev }
        const actionIndex = updatedActions.addActions.findIndex(
          (action) => action.tempId === tempId
        )

        if (actionIndex >= 0) {
          const action = updatedActions.addActions[actionIndex]
          const stockInfo = getStockInfo(action.product)
          const hasStockLimit =
            action.product.stockLimit && stockInfo.shouldShowStock
          const availableStock = getAvailableStock(action.product.id)
          const maxQuantity =
            hasStockLimit && action.product.type !== 'SERVICE'
              ? availableStock + action.quantity // Add current quantity back for max calculation
              : Infinity

          const finalQuantity = Math.min(newQuantity, maxQuantity)

          updatedActions.addActions[actionIndex].quantity = finalQuantity
        }

        return updatedActions
      })
    },
    [handleRemoveAddAction, getAvailableStock, getStockInfo]
  )

  // Handle updating price for add action
  const handleUpdateAddPrice = useCallback(
    (
      tempId: string,
      priceUnitary: { amount: number; codeCurrency: string }
    ) => {
      setActions((prev) => ({
        ...prev,
        addActions: prev.addActions.map((action) =>
          action.tempId === tempId ? { ...action, priceUnitary } : action
        ),
      }))
    },
    []
  )

  // Handle modifying existing product
  const handleModifyProduct = useCallback(
    (
      originalProduct: SelledProductsInterface,
      newQuantity: number,
      newPrice: number
    ) => {
      const currency = originalProduct.priceUnitary.codeCurrency

      setActions((prev) => {
        const updatedActions = { ...prev }

        // Remove any existing modify action for this product
        updatedActions.modifyActions = updatedActions.modifyActions.filter(
          (action) => action.originalProduct.id !== originalProduct.id
        )

        // Remove any existing delete action for this product (if restoring)
        updatedActions.deleteActions = updatedActions.deleteActions.filter(
          (action) => action.originalProduct.id !== originalProduct.id
        )

        // Check if there are actual changes from original values
        const hasQuantityChange = newQuantity !== originalProduct.quantity
        const hasPriceChange =
          Math.abs(newPrice - originalProduct.priceUnitary.amount) > 0.001 // Use small epsilon for float comparison

        // Only add modify action if there are actual changes
        if (hasQuantityChange || hasPriceChange) {
          // Validate stock if quantity is increasing
          if (newQuantity > originalProduct.quantity) {
            const fullProduct = getFullProductData(originalProduct.productId)
            if (fullProduct) {
              const stockInfo = getStockInfo(fullProduct)
              const hasStockLimit =
                fullProduct.stockLimit && stockInfo.shouldShowStock

              if (hasStockLimit && fullProduct?.type !== 'SERVICE') {
                const availableStock = getAvailableStock(
                  originalProduct.productId
                )
                const quantityIncrease = newQuantity - originalProduct.quantity

                if (quantityIncrease > availableStock) {
                  // Set quantity to maximum available
                  newQuantity = originalProduct.quantity + availableStock
                }
              }
            }
          }

          // Add the modify action with validated quantity
          const modifyAction: ModifyProductAction = {
            type: 'MODIFY',
            originalProduct,
            newQuantity,
            newPrice,
            currency,
          }
          updatedActions.modifyActions.push(modifyAction)
        }
        // If no changes, the action arrays are already cleaned, so product returns to original state

        return updatedActions
      })
    },
    [getFullProductData, getAvailableStock, getStockInfo]
  )

  // Handle deleting existing product (full or partial)
  const handleDeleteProduct = useCallback(
    (originalProduct: SelledProductsInterface, quantityToDelete?: number) => {
      // Special case: quantityToDelete === 0 means remove delete action entirely
      if (quantityToDelete === 0) {
        setActions((prev) => ({
          ...prev,
          deleteActions: prev.deleteActions.filter(
            (action) => action.originalProduct.id !== originalProduct.id
          ),
        }))
        return
      }

      const deleteQuantity = quantityToDelete || originalProduct.quantity

      // Ensure we don't delete more than available
      const maxDeleteQuantity = originalProduct.quantity
      const finalDeleteQuantity = Math.min(deleteQuantity, maxDeleteQuantity)

      setActions((prev) => {
        const updatedActions = { ...prev }

        // Remove any existing modify action for this product (delete takes precedence)
        updatedActions.modifyActions = updatedActions.modifyActions.filter(
          (action) => action.originalProduct.id !== originalProduct.id
        )

        // Remove any existing delete action for this product
        updatedActions.deleteActions = updatedActions.deleteActions.filter(
          (action) => action.originalProduct.id !== originalProduct.id
        )

        // Add delete action only if quantity > 0
        if (finalDeleteQuantity > 0) {
          const deleteAction: DeleteProductAction = {
            type: 'DELETE',
            originalProduct,
            quantity: finalDeleteQuantity,
          }
          updatedActions.deleteActions.push(deleteAction)
        }

        return updatedActions
      })
    },
    []
  )

  // Handle focusing a product in the stock view
  const handleFocusProduct = useCallback((productId: number) => {
    setFocusedProductId(productId)

    // Clear focus after 3 seconds for better UX
    setTimeout(() => {
      setFocusedProductId(undefined)
    }, 3000)
  }, [])

  // Calculate totals considering all actions
  const totals = useMemo(() => {
    const currencyTotals: Record<string, number> = {}
    let totalWeight = 0
    let totalVolume = 0

    // Calculate from existing products (considering modifications and partial deletions)
    existingProducts.forEach((product) => {
      const deleteAction = actions.deleteActions.find(
        (action) => action.originalProduct.id === product.id
      )
      const modifyAction = actions.modifyActions.find(
        (action) => action.originalProduct.id === product.id
      )

      // Calculate the effective quantity after modifications and deletions
      let effectiveQuantity = product.quantity
      let effectivePrice = product.priceUnitary.amount

      // Apply modifications first
      if (modifyAction) {
        effectiveQuantity = modifyAction.newQuantity
        effectivePrice = modifyAction.newPrice
      }

      // Apply partial deletions
      if (deleteAction) {
        effectiveQuantity = Math.max(
          0,
          effectiveQuantity - deleteAction.quantity
        )
      }

      // Only count if there's remaining quantity
      if (effectiveQuantity > 0) {
        const currency = product.priceUnitary.codeCurrency

        currencyTotals[currency] =
          (currencyTotals[currency] || 0) + effectiveQuantity * effectivePrice

        const fullProduct = getFullProductData(product.productId)
        totalWeight += (fullProduct?.netWeight || 0) * effectiveQuantity
        totalVolume += (fullProduct?.cubaje || 0) * effectiveQuantity
      }
    })

    // Add totals from add actions
    actions.addActions.forEach((action) => {
      currencyTotals[action.priceUnitary.codeCurrency] =
        (currencyTotals[action.priceUnitary.codeCurrency] || 0) +
        action.quantity * action.priceUnitary.amount

      totalWeight += (action.product.netWeight || 0) * action.quantity
      totalVolume += (action.product.cubaje || 0) * action.quantity
    })

    return { currencyTotals, totalWeight, totalVolume }
  }, [existingProducts, actions, getFullProductData])

  // Handle save
  const handleSave = useCallback(() => {
    onSave(actions, Number(selectedPointOfSale))
    onOpenChange(false)
  }, [actions, selectedPointOfSale, onSave, onOpenChange])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault()
        if (open) {
          handleSave()
        }
      }
    }

    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, handleSave])

  // Handle search change
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  // Handle category change
  const handleCategoryChange = useCallback((value: string) => {
    setSelectedCategory(value)
  }, [])

  // Enhanced function to calculate stock considering pending actions and document type
  const getEnhancedStockInfo = useCallback(
    (product: InvoiceFullProductData): EnhancedStockInfo => {
      const baseStockInfo = getStockInfo(product)

      // Start with the base stock information
      let visualStockReal = baseStockInfo.stockReal
      let visualStockReserved = baseStockInfo.stockReserved
      let visualStockAvailable = baseStockInfo.stockAvailable

      // If stock shouldn't be shown, return early with base values
      if (!baseStockInfo.shouldShowStock) {
        return {
          ...baseStockInfo,
          visualStockReal,
          visualStockReserved,
          visualStockAvailable,
        }
      }

      // Calculate the net effect of all actions for this product
      // The logic differs based on whether we're making real or temporary changes
      let netQuantityChange = 0

      // 1. Calculate existing products impact
      const existingProductsForThisProduct = existingProducts.filter(
        (p) => p.productId === product.id
      )

      let currentExistingQuantity = 0
      let finalExistingQuantity = 0

      existingProductsForThisProduct.forEach((existingProduct) => {
        const deleteAction = actions.deleteActions.find(
          (action) => action.originalProduct.id === existingProduct.id
        )
        const modifyAction = actions.modifyActions.find(
          (action) => action.originalProduct.id === existingProduct.id
        )

        // Current quantity from this existing product
        currentExistingQuantity += existingProduct.quantity

        // Final quantity after actions
        if (deleteAction) {
          // Being deleted: remaining quantity after deletion
          finalExistingQuantity += Math.max(
            0,
            existingProduct.quantity - deleteAction.quantity
          )
        } else if (modifyAction) {
          // Being modified: new quantity
          finalExistingQuantity += modifyAction.newQuantity
        } else {
          // Not changed: same quantity
          finalExistingQuantity += existingProduct.quantity
        }
      })

      // 2. Calculate from add actions
      const addActionsForProduct = actions.addActions.filter(
        (action) => action.product.id === product.id
      )
      const totalAddedQuantity = addActionsForProduct.reduce(
        (sum, action) => sum + action.quantity,
        0
      )

      // 3. Calculate net change based on mode
      let existingProductsChange = 0
      if (isTemporaryChanges) {
        // TEMPORARY MODE: Existing products are pre-selected items, treat as visual consumption
        // Show the total planned consumption (existing + new additions)
        netQuantityChange = finalExistingQuantity + totalAddedQuantity
      } else {
        // REAL CHANGES MODE: baseStockInfo already reflects existing products
        // Only show the change from current state
        existingProductsChange = finalExistingQuantity - currentExistingQuantity
        netQuantityChange = existingProductsChange + totalAddedQuantity
      }

      // Apply visual changes based on document type and temporary vs real changes
      if (isTemporaryChanges) {
        // TEMPORARY CHANGES MODE (ProductSelectionStep)
        // Show the planned impact without actually consuming stock
        // Display what will remain available after the planned changes
        if (documentType === 'factura') {
          // For invoices, show planned real stock consumption
          visualStockReal = baseStockInfo.stockReal // Keep original real stock
          visualStockAvailable = Math.max(
            0,
            baseStockInfo.stockAvailable - netQuantityChange
          )
          visualStockReserved = baseStockInfo.stockReserved // Keep original reserved
        } else {
          // For pre-invoices, show planned reserved stock increase
          visualStockReal = baseStockInfo.stockReal // Keep original real stock
          visualStockReserved = Math.max(
            0,
            baseStockInfo.stockReserved + netQuantityChange
          ) // Show reserved increase
          visualStockAvailable = Math.max(
            0,
            baseStockInfo.stockReal - visualStockReserved
          ) // Recalculate available based on new reserved
        }
      } else {
        // REAL CHANGES MODE (ProductsCard)
        // Show actual stock consumption as before
        if (documentType === 'factura') {
          // For invoices, modify the real stock visually
          // netQuantityChange > 0 means more products consumed, reduce real stock
          // netQuantityChange < 0 means products freed up, increase real stock
          visualStockReal = Math.max(
            0,
            baseStockInfo.stockReal - netQuantityChange
          )
          visualStockAvailable = Math.max(
            0,
            visualStockReal - baseStockInfo.stockReserved
          )
        } else {
          // For pre-invoices, modify the reserved stock visually
          // netQuantityChange > 0 means more products reserved, increase reserved stock
          // netQuantityChange < 0 means reservations freed up, decrease reserved stock
          visualStockReserved = Math.max(
            0,
            baseStockInfo.stockReserved + netQuantityChange
          )
          visualStockAvailable = Math.max(
            0,
            baseStockInfo.stockReal - visualStockReserved
          )
        }
      }

      return {
        ...baseStockInfo,
        visualStockReal,
        visualStockReserved,
        visualStockAvailable,
      }
    },
    [getStockInfo, actions, existingProducts, documentType, isTemporaryChanges]
  )

  // Overall error state - only show error drawer for critical failures
  const isCriticalError = isErrorBusinessConfigs

  if (isCriticalError) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} dismissible={true}>
        <DrawerContent className="mx-auto flex h-[95vh] max-h-[95vh] max-w-7xl flex-col">
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="mb-2 text-sm text-destructive">
                Error crítico al cargar configuraciones.
              </p>
              <p className="text-xs text-muted-foreground">
                Intenta recargar la página.
              </p>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} dismissible={true}>
      <DrawerContent
        className="mx-auto flex h-[95vh] max-h-[95vh] max-w-7xl flex-col"
        overlayClassName={overlayClassName}
      >
        <DrawerHeader className="flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <DrawerTitle>Gestionar Productos</DrawerTitle>
              <DrawerDescription>
                Añade o modifica los productos de la {documentType}.
              </DrawerDescription>
            </div>
            <TipsPanel className="mt-1" />
          </div>
        </DrawerHeader>

        <div className="flex-grow overflow-hidden">
          <div className="grid h-full grid-cols-1 gap-6 p-4 md:grid-cols-12">
            <ManagerStock
              products={products}
              isLoadingProducts={isLoadingProducts}
              isErrorProducts={isErrorProducts}
              isFetchingNextPage={isFetchingNextPage}
              canLoadMore={canLoadMore}
              fetchNextPage={fetchNextPage}
              refetchProducts={refetchProducts}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              availableCategories={salesCategories || []}
              isLoadingSalesCategories={isLoadingSalesCategories}
              isErrorSalesCategories={isErrorSalesCategories}
              onAddProduct={handleAddProduct}
              getAvailableStock={getAvailableStock}
              focusedProductId={focusedProductId}
              ensureProductLoaded={ensureProductLoaded}
              getStockInfo={getStockInfo}
              getEnhancedStockInfo={getEnhancedStockInfo}
              formatStockNumber={formatStockNumber}
              isTemporaryChanges={isTemporaryChanges}
              priceSystemId={priceSystemId}
              documentType={documentType}
              className="md:col-span-5"
            />
            <ProductActionsView
              products={products ?? []}
              existingProducts={existingProducts}
              actions={actions}
              onRemoveAddAction={handleRemoveAddAction}
              onUpdateAddQuantity={handleUpdateAddQuantity}
              onUpdateAddPrice={handleUpdateAddPrice}
              onModifyProduct={handleModifyProduct}
              onDeleteProduct={handleDeleteProduct}
              onAddProduct={handleAddProduct}
              currencyTotals={totals.currencyTotals}
              totalWeight={totals.totalWeight}
              totalVolume={totals.totalVolume}
              getAvailableStock={getAvailableStock}
              onFocusProduct={handleFocusProduct}
              documentType={documentType}
              isTemporaryChanges={isTemporaryChanges}
              className="md:col-span-7"
            />
          </div>
        </div>

        <DrawerFooter className="flex-shrink-0 flex-row justify-end gap-2 border-t bg-background">
          <DrawerClose asChild>
            <Button variant="outline">Cancelar</Button>
          </DrawerClose>
          <Button onClick={handleSave}>Guardar Cambios</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
